import { createAsyncThunk } from '@reduxjs/toolkit';
import { getFloorplanAiReading } from '../services/getFloorplanAiReading.service';
import { postFloorplanAiReading } from '../services/postFloorplanAiReading.service';

/**
 * The run-wide refusals of a reading: the assistant's door (no permission,
 * no employer picked, the month's allowance spent) and a model that is not
 * configured. Reading the next drawing would be refused the same way.
 */
export const AI_READING_RUN_STOPPING_CODES = Object.freeze([
  'nerobotti_no_permission',
  'nerobotti_no_business',
  'nerobotti_allowance_exhausted',
  'floorplan_reading_unavailable',
]);

export const isAiReadingRunStoppingCode = (code) =>
  AI_READING_RUN_STOPPING_CODES.includes(code);

/** How often a background reading is asked after. */
export const AI_READING_POLL_MS = 3000;
/**
 * How long a background reading is waited for; the API itself calls one
 * lost after 12 minutes.
 */
export const AI_READING_WAIT_MS = 13 * 60 * 1000;
/** Failed asks in a row (a dropped connection) before giving up. */
const MAX_POLL_FAILURES = 4;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const readingError = (status, code, message) =>
  Object.assign(new Error(message || code || 'AI reading failed'), {
    response: { status, data: { code, message } },
  });

/**
 * Waits for a reading the API runs in the background (a long one outlasts
 * the 30 s a request may take): asks after the job until it is done, then
 * answers the row or throws the reading's refusal as an axios-like error.
 */
const waitForReading = async (projectId, floorplanId, jobId, pollMs) => {
  const giveUpAt = Date.now() + AI_READING_WAIT_MS;
  let failures = 0;
  while (Date.now() < giveUpAt) {
    await wait(pollMs);
    let answer;
    try {
      answer = await getFloorplanAiReading(projectId, floorplanId);
      failures = 0;
    } catch (error) {
      failures += 1;
      if (failures >= MAX_POLL_FAILURES) throw error;
      continue;
    }
    const job = answer && answer.job;
    if (!job || job.id !== jobId) {
      throw readingError(
        409,
        'floorplan_reading_replaced',
        'Another reading of this drawing was started.'
      );
    }
    if (job.status === 'done') {
      const result = job.result || {};
      if (result.status === 200) return answer.floorplan;
      throw readingError(
        Number.isFinite(result.status) ? result.status : null,
        result.code || null,
        result.message || null
      );
    }
  }
  throw readingError(
    504,
    'floorplan_reading_lost',
    'The reading did not finish. Try again.'
  );
};

/**
 * Reads a drawing with the AI and stores the row it answers with, whose
 * `aiReading` the 3D view and the forms offer as guesses where the row says
 * nothing (withGuessedDrawingFields). Every call spends the company's AI
 * allowance; the API refuses once it is spent.
 *
 * Given `levelMarks` (findLevelMarks: where each level mark is on the
 * image), the values written on them are read as well, from close-ups, and
 * kept as `aiReading.levelMarks`; with `levelMarksOnly`, only those are read
 * and added to the row's reading.
 *
 * Given `floorLevelArea` (a floor plan's building box, fractions of the
 * image), the storey's level is read from enlarged pieces of it and kept as
 * `aiReading.floorLevel`; with `floorLevelOnly`, only that is read.
 *
 * Given `sectionLevelArea` (a section's drawing box) and `sectionFloors`
 * (the project's floor plans: id, name, level), only the section's level
 * marks are read, and the level each floor plan's storey has on it, kept as
 * `aiReading.sectionLevels`.
 *
 * Where the API reads in the background it answers a job at once, and the
 * thunk waits for the job's end (`pollMs` apart, for tests).
 *
 * Rejects with `{ status, code, message }`: the API's code and message where
 * it gave them (`nerobotti_*` at the door, `floorplan_image_*` for the
 * image, `floorplan_reading_*` for the reading), nulls otherwise.
 */
export const readFloorplanWithAi = createAsyncThunk(
  'floorplans/readFloorplanWithAi',
  async (
    {
      projectId,
      floorplanId,
      levelMarks = null,
      levelMarksOnly = false,
      floorLevelArea = null,
      floorLevelOnly = false,
      sectionLevelArea = null,
      sectionFloors = null,
      pollMs = AI_READING_POLL_MS,
    },
    { rejectWithValue }
  ) => {
    const body = {};
    if (sectionLevelArea && Array.isArray(sectionFloors)) {
      const { x0, y0, x1, y1 } = sectionLevelArea;
      body.sectionLevelsOnly = true;
      body.sectionLevelArea = { x0, y0, x1, y1 };
      body.floors = sectionFloors.map(({ id, name, level }) => ({
        id,
        name,
        level,
      }));
    }
    if (levelMarks && levelMarks.length > 0) {
      body.levelMarks = levelMarks.map(({ x, y, box }) => ({ x, y, box }));
      if (levelMarksOnly) body.levelMarksOnly = true;
    }
    if (floorLevelArea) {
      const { x0, y0, x1, y1 } = floorLevelArea;
      body.floorLevelArea = { x0, y0, x1, y1 };
      if (floorLevelOnly) body.floorLevelOnly = true;
    }
    try {
      const answer = await postFloorplanAiReading(projectId, floorplanId, body);
      if (answer && typeof answer.jobId === 'string') {
        return await waitForReading(
          projectId,
          floorplanId,
          answer.jobId,
          pollMs
        );
      }
      return answer;
    } catch (error) {
      const response = error && error.response;
      const data = (response && response.data) || {};
      return rejectWithValue({
        status:
          response && Number.isFinite(response.status) ? response.status : null,
        code: typeof data.code === 'string' ? data.code : null,
        message:
          typeof data.message === 'string'
            ? data.message
            : (error && error.message) || null,
      });
    }
  }
);
